import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { getSkus, createSku, deleteSku, getVariants, createVariant, updateSku, importSampleCsvUrl, backfillDefaultVariants, autoFillDimsFromCartons, bulkUpdateSkuPrice, getStockoutRisk, setVariantActive, bulkSetVariantsActive } from '../lib/api';
import { Plus, MagnifyingGlass, Trash, Eye, Barcode, Package, PencilSimple, Check, Truck, WarningCircle, DownloadSimple, X, Lightning, Ruler, UploadSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import SkuImageUploader from '../components/SkuImageUploader';
import BarcodeScanner from '../components/BarcodeScanner';
import { printShelfLabel } from '../components/ShelfLabel';
import AbcChip from '../components/AbcChip';
import { useAuth } from '../context/AuthContext';
import BulkActionsBar from '../components/BulkActionsBar';
import BulkPriceDialog from '../components/BulkPriceDialog';
import PriceHistoryDialog from '../components/PriceHistoryDialog';
import StockHistoryDialog from '../components/StockHistoryDialog';
import CreateSkuDialog from '../components/CreateSkuDialog';
import ManageSkuUomsDialog from '../components/ManageSkuUomsDialog';
import CreateVariantDialog from '../components/CreateVariantDialog';
import SkuTierPricePreview from '../components/SkuTierPricePreview';
import BulkScanOnboard from '../components/BulkScanOnboard';
import SkuImageDialog from '../components/SkuImageDialog';
import DispatchReadyToolbar from '../components/DispatchReadyToolbar';
import useBulkSelection from '../lib/useBulkSelection';
import { ImageSquare, Camera, Printer, CurrencyDollar, ClockClockwise, ChartLineUp } from '@phosphor-icons/react';

import { toast } from 'sonner';

export default function Inventory() {
  const { isAdmin } = useAuth();
  const [skus, setSkus] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateSku, setShowCreateSku] = useState(false);
  const [showCreateVariant, setShowCreateVariant] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  
  const [newSku, setNewSku] = useState({
    name: '', description: '', inventory_class: 'finished_good',
    unit_price: 0, landed_cost: 0,
    weight_kg: 0, volume_m3: 0,
    sku_code: '', barcode: '',
    // Each-level dimensions (cm). volume_m3 is auto-derived if all 3 set.
    length_cm: '', width_cm: '', height_cm: '',
    // Inner-pack UOM
    units_per_inner: '', inner_length_cm: '', inner_width_cm: '', inner_height_cm: '', inner_weight_kg: '',
    // Carton
    case_pack: '', case_weight_kg: '', case_volume_m3: '',
    case_length_cm: '', case_width_cm: '', case_height_cm: '',
    // iter149 — fractional qty + UoM (defaults: discrete + each)
    allow_fractional: false, unit_of_measure: 'each',
    // iter163 — reorder thresholds (min/max). Empty = "not set".
    reorder_point: '', reorder_max: '',
    // iter164 — alternate UOM conversions (e.g. [{code:"L", factor:1000}]).
    uom_conversions: [],
  });
  const [newVariant, setNewVariant] = useState({ sku_id: '', size: '', color: '', style: '', barcode: '' });
  // Inline edit state for sku_code, barcode, weight_kg, volume_m3, unit_price, landed_cost
  const [editRow, setEditRow] = useState(null); // sku.id
  const [editSkuCode, setEditSkuCode] = useState('');
  const [editBarcode, setEditBarcode] = useState('');
  const [editWeight, setEditWeight] = useState('');
  const [editVolume, setEditVolume] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editLanded, setEditLanded] = useState('');
  // iter168 — inline edit of reorder thresholds (min/max) per row.
  const [editReorderMin, setEditReorderMin] = useState('');
  const [editReorderMax, setEditReorderMax] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // Bulk scan & onboard state
  const [bulkScanOpen, setBulkScanOpen] = useState(false);
  const [bulkOnboarded, setBulkOnboarded] = useState([]); // [{sku_code, barcode, name}, …] this session
  // Multi-select state for batch print labels — lives in a dedicated hook
  const selection = useBulkSelection();
  // Iter133n — separate selection state for the Variants tab so SKU and
  // variant bulk actions don't collide. The two bars are gated on which
  // tab the user is in (only one ever shows actionable count).
  const variantSelection = useBulkSelection();
  const [variantBulkBusy, setVariantBulkBusy] = useState(false);
  // Iter 109 — bulk price dialog state
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  // Iter 110 — per-row price history dialog
  const [priceHistorySku, setPriceHistorySku] = useState(null);
  // Iter 112 — variant stock-history dialog
  const [stockHistoryVariant, setStockHistoryVariant] = useState(null);
  // Iter 113 — stockout-risk map keyed by sku_id (only at-risk SKUs are returned)
  const [stockoutRisk, setStockoutRisk] = useState({});
  // Mobile bottom-sheet edit state (phone-friendly alternative to inline edit row)
  const [mobileEditSku, setMobileEditSku] = useState(null);
  const [mobileEditFields, setMobileEditFields] = useState({ sku_code: '', barcode: '', weight_kg: 0, volume_m3: 0, unit_price: 0, landed_cost: 0 });
  const [mobileEditSaving, setMobileEditSaving] = useState(false);
  const [pendingMobileSku, setPendingMobileSku] = useState(null); // stashed while scanner is open
  // SKU Image dialog state
  const [imageDialogSku, setImageDialogSku] = useState(null);
  // iter164 — Manage UOMs dialog state
  const [manageUomsSku, setManageUomsSku] = useState(null);
  // Barcode scanner state — scanTarget: 'new' = populate new-SKU dialog; <sku_id> = inline edit row; 'bulk' = bulk onboard
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState(null);
  // Dispatch-ready filter: show only SKUs / variants whose parent SKU is missing weight or volume
  const [showMissingOnly, setShowMissingOnly] = useState(false);
  // Iter 133m — Show inactive (deactivated) variants. Default OFF so the
  // catalog is "clean" by default, matching small-business expectations.
  const [showInactive, setShowInactive] = useState(false);
  const [togglingActive, setTogglingActive] = useState(null);

  const isDispatchReady = (sku) => (sku?.weight_kg || 0) > 0 && (sku?.volume_m3 || 0) > 0;
  const skusMap = React.useMemo(() => Object.fromEntries(skus.map(s => [s.id, s])), [skus]);

  // Multi-select helpers
  const printSelectedLabels = () => {
    const list = selection.ids.map((id) => skusMap[id]).filter(Boolean);
    if (list.length === 0) return;
    printShelfLabel(list);
  };

  const handleBulkPriceApply = async ({ mode, value }) => {
    try {
      const res = await bulkUpdateSkuPrice({ sku_ids: selection.ids, mode, value });
      const { updated = 0 } = res.data || {};
      toast.success(`${updated} SKU${updated === 1 ? '' : 's'} repriced.`);
      selection.clear();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk reprice failed');
    }
  };
  const missingCount = skus.filter(s => !isDispatchReady(s)).length;
  // Iter 105 — orphan SKU detector. A SKU with `variant_count === 0` cannot
  // be added to an order (the order picker walks variants). Count them up so
  // we can surface a "Fix orphan SKUs" CTA in the toolbar.
  const orphanCount = skus.filter(s => (s.variant_count ?? 0) === 0).length;
  // SKUs that have carton info but are still missing weight or volume — these
  // are "ready to auto-fill" candidates for the dim backfill button.
  const cartonFillCount = skus.filter(s => {
    const cp = parseInt(s.case_pack || 0, 10);
    if (cp <= 0) return false;
    const hasCaseW = (s.case_weight_kg || 0) > 0;
    const hasCaseV = (s.case_volume_m3 || 0) > 0;
    if (!hasCaseW && !hasCaseV) return false;
    const missingW = !s.weight_kg && hasCaseW;
    const missingV = !s.volume_m3 && hasCaseV;
    return missingW || missingV;
  }).length;
  const filteredSkus = showMissingOnly ? skus.filter(s => !isDispatchReady(s)) : skus;
  const filteredVariants = (() => {
    let v = variants;
    if (showMissingOnly) v = v.filter(x => !isDispatchReady(skusMap[x.sku_id]));
    if (!showInactive) v = v.filter(x => x.is_active !== false);
    return v;
  })();
  const inactiveCount = variants.filter(v => v.is_active === false).length;

  // Optimistic toggle. We update local state immediately so the switch
  // doesn't lag, then sync with the backend; revert on error.
  const toggleVariantActive = async (variant) => {
    const next = variant.is_active === false; // currently inactive -> activate
    setTogglingActive(variant.id);
    setVariants(prev => prev.map(v => v.id === variant.id ? { ...v, is_active: next } : v));
    try {
      await setVariantActive(variant.id, next);
      toast.success(next ? 'Variant activated' : 'Variant deactivated — hidden from order picker');
    } catch (e) {
      // Revert
      setVariants(prev => prev.map(v => v.id === variant.id ? { ...v, is_active: !next } : v));
      toast.error(e?.response?.data?.detail || 'Failed to update variant');
    } finally {
      setTogglingActive(null);
    }
  };

  // Bulk handler — reaches backend POST /variants/bulk-active. Optimistic:
  // we flip all selected rows immediately, then reconcile the response
  // (revert on error). Used by the BulkActionsBar Deactivate / Activate
  // buttons on the Variants tab.
  const bulkToggleVariants = async (nextActive) => {
    const ids = variantSelection.ids;
    if (!ids.length) return;
    const verb = nextActive ? 'activated' : 'deactivated';
    if (!nextActive && !window.confirm(
      `Deactivate ${ids.length} variant${ids.length === 1 ? '' : 's'}?\n\n` +
      `They'll stay in the system but be hidden from the order picker. ` +
      `You can reactivate any time.`
    )) return;
    setVariantBulkBusy(true);
    const prevState = ids.map((id) => {
      const v = variants.find((x) => x.id === id);
      return [id, v?.is_active !== false];
    });
    setVariants((prev) => prev.map((v) => ids.includes(v.id) ? { ...v, is_active: nextActive } : v));
    try {
      const r = await bulkSetVariantsActive(ids, nextActive);
      const updated = r.data?.updated ?? 0;
      const skipped = (r.data?.invalid?.length || 0) + (r.data?.missing?.length || 0);
      toast.success(
        `${updated} variant${updated === 1 ? '' : 's'} ${verb}` +
        (skipped > 0 ? ` · ${skipped} skipped (deleted or invalid)` : ''),
      );
      variantSelection.clear();
    } catch (e) {
      // Revert all rows we touched
      setVariants((prev) => prev.map((v) => {
        const prior = prevState.find(([id]) => id === v.id);
        return prior ? { ...v, is_active: prior[1] } : v;
      }));
      toast.error(e?.response?.data?.detail || 'Bulk update failed');
    } finally {
      setVariantBulkBusy(false);
    }
  };

  // Mixed-state helper: are any of the selected variants currently active?
  // Used to label the bulk button (Deactivate vs Activate).
  const anySelectedActive = variantSelection.ids.some((id) => {
    const v = variants.find((x) => x.id === id);
    return v && v.is_active !== false;
  });

  const fetchData = async () => {
    try {
      const [skuRes, varRes, riskRes] = await Promise.all([
        getSkus({ search }),
        getVariants({ include_inactive: true }),
        getStockoutRisk({ days_window: 14, lead_time_days: 14 }).catch(() => ({ data: { rows: [] } })),
      ]);
      setSkus(skuRes.data);
      setVariants(varRes.data);
      const map = {};
      for (const r of (riskRes.data?.rows || [])) {
        if (r.sku_id) map[r.sku_id] = r;
      }
      setStockoutRisk(map);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [search]);

  const handleCreateSku = async (e) => {
    e.preventDefault();
    try {
      // Coerce text inputs (left empty for clarity) to numbers/null before
      // sending. Backend auto-derives volume_m3 from L/W/H when all three
      // are present, so we don't have to pre-compute on the client.
      const numOrNull = (v) => v === '' || v === null || v === undefined ? null : Number(v);
      const intOrNull = (v) => v === '' || v === null || v === undefined ? null : parseInt(v, 10);
      const payload = {
        ...newSku,
        // Each dims
        length_cm: numOrNull(newSku.length_cm),
        width_cm: numOrNull(newSku.width_cm),
        height_cm: numOrNull(newSku.height_cm),
        // Inner UOM
        units_per_inner: intOrNull(newSku.units_per_inner),
        inner_length_cm: numOrNull(newSku.inner_length_cm),
        inner_width_cm: numOrNull(newSku.inner_width_cm),
        inner_height_cm: numOrNull(newSku.inner_height_cm),
        inner_weight_kg: numOrNull(newSku.inner_weight_kg),
        // Carton UOM
        case_pack: intOrNull(newSku.case_pack),
        case_weight_kg: numOrNull(newSku.case_weight_kg),
        case_volume_m3: numOrNull(newSku.case_volume_m3),
        case_length_cm: numOrNull(newSku.case_length_cm),
        case_width_cm: numOrNull(newSku.case_width_cm),
        case_height_cm: numOrNull(newSku.case_height_cm),
        // iter149 — fractional qty + UoM
        allow_fractional: !!newSku.allow_fractional,
        unit_of_measure: (newSku.unit_of_measure || 'each').trim().toLowerCase(),
        // iter163 — reorder thresholds
        reorder_point: intOrNull(newSku.reorder_point),
        reorder_max: intOrNull(newSku.reorder_max),
        // iter164 — UOM conversions (filter empty rows, coerce factor + optional price)
        uom_conversions: (newSku.uom_conversions || [])
          .map((r) => {
            const factor = parseFloat(r.factor);
            const out = {
              code: (r.code || '').trim().toLowerCase(),
              factor,
              label: (r.label || '').trim() || null,
            };
            const price = parseFloat(r.unit_price);
            if (Number.isFinite(price) && price >= 0) out.unit_price = price;
            return out;
          })
          .filter((r) => r.code && Number.isFinite(r.factor) && r.factor > 0),
      };
      await createSku(payload);
      setShowCreateSku(false);
      setNewSku({
        name: '', description: '', inventory_class: 'finished_good',
        unit_price: 0, landed_cost: 0, weight_kg: 0, volume_m3: 0,
        sku_code: '', barcode: '',
        length_cm: '', width_cm: '', height_cm: '',
        units_per_inner: '', inner_length_cm: '', inner_width_cm: '', inner_height_cm: '', inner_weight_kg: '',
        case_pack: '', case_weight_kg: '', case_volume_m3: '',
        case_length_cm: '', case_width_cm: '', case_height_cm: '',
        allow_fractional: false, unit_of_measure: 'each',
        reorder_point: '', reorder_max: '',
        uom_conversions: [],
      });
      fetchData();
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(detail);
    }
  };

  const handleBackfillOrphans = async () => {
    if (!window.confirm(`Auto-create a default variant for the ${orphanCount} SKU${orphanCount === 1 ? '' : 's'} that currently have none? This makes them sellable on orders. Skipped if a SKU already has at least one variant.`)) return;
    try {
      const res = await backfillDefaultVariants();
      const { created = 0 } = res.data || {};
      toast.success(`${created} default variant${created === 1 ? '' : 's'} created — orphan SKUs are now sellable.`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    }
  };

  const handleAutoFillDims = async () => {
    if (!window.confirm(`Auto-fill weight + volume for the ${cartonFillCount} SKU${cartonFillCount === 1 ? '' : 's'} that have carton info but missing per-unit dims? Existing measured values are NEVER overwritten.`)) return;
    try {
      const res = await autoFillDimsFromCartons();
      const { updated = 0, weight_filled = 0, volume_filled = 0 } = res.data || {};
      toast.success(`${updated} SKU${updated === 1 ? '' : 's'} updated — ${weight_filled} weight, ${volume_filled} volume filled.`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-fill failed');
    }
  };

  const handleCreateVariant = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newVariant };
      if (!payload.barcode?.trim()) delete payload.barcode;
      await createVariant(payload);
      setShowCreateVariant(false);
      setNewVariant({ sku_id: '', size: '', color: '', style: '', barcode: '' });
      toast.success('Variant created');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create variant');
    }
  };

  const handleDeleteSku = async (id) => {
    if (window.confirm('Delete this SKU?')) {
      await deleteSku(id);
      fetchData();
    }
  };

  return (
    <Layout>
      <div data-testid="inventory-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Inventory</h1>
            <p className="text-muted-foreground">Manage SKUs, variants, and stock levels</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              asChild
              variant="outline"
              data-testid="bulk-import-skus-btn"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              title="Bulk-import SKUs from a CSV file — download a template first if you need one"
            >
              <Link to="/csv-import?entity=skus">
                <UploadSimple size={16} className="mr-2" /> Bulk import CSV
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => { setBulkOnboarded([]); setBulkScanOpen(true); }}
              data-testid="bulk-scan-onboard-btn"
              className="border-brand text-brand hover:bg-brand/5"
              title="Scan multiple barcodes in a row — each scan creates a draft SKU"
            >
              <Lightning size={16} weight="fill" className="mr-2" /> Scan &amp; Onboard
            </Button>
            <CreateSkuDialog
              open={showCreateSku}
              onOpenChange={setShowCreateSku}
              newSku={newSku}
              setNewSku={setNewSku}
              onSubmit={handleCreateSku}
              onScanBarcode={() => {
                // Close this dialog so its overlay doesn't trap pointer events
                // behind the scanner. We re-open after scan/cancel — newSku
                // state survives because it lives in the parent.
                setShowCreateSku(false);
                setScanTarget('new');
                setScannerOpen(true);
              }}
            />
            <CreateVariantDialog
              open={showCreateVariant}
              onOpenChange={setShowCreateVariant}
              newVariant={newVariant}
              setNewVariant={setNewVariant}
              skus={skus}
              onSubmit={handleCreateVariant}
              onScanBarcode={() => {
                setShowCreateVariant(false);
                setScanTarget('newVariant');
                setScannerOpen(true);
              }}
            />
          </div>
        </div>

        <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative max-w-md flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search SKUs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="inventory-search"
            />
          </div>
          <DispatchReadyToolbar
            showMissingOnly={showMissingOnly}
            onToggleMissing={() => setShowMissingOnly(v => !v)}
            missingCount={missingCount}
            orphanCount={orphanCount}
            onBackfillOrphans={handleBackfillOrphans}
            cartonFillCount={cartonFillCount}
            onAutoFillDims={handleAutoFillDims}
            missingCsvHref={importSampleCsvUrl('skus', true, { missingDimsOnly: true })}
          />
        </div>

        <Tabs defaultValue="skus" className="bg-card border border-border rounded-lg overflow-hidden">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="skus" className="flex-1 rounded-none data-[state=active]:bg-accent data-[state=active]:border-b-2 data-[state=active]:border-brand">SKUs ({filteredSkus.length}{showMissingOnly ? ` / ${skus.length}` : ''})</TabsTrigger>
            <TabsTrigger value="variants" className="flex-1 rounded-none data-[state=active]:bg-accent data-[state=active]:border-b-2 data-[state=active]:border-brand">Variants ({filteredVariants.length}{showMissingOnly ? ` / ${variants.length}` : ''})</TabsTrigger>
          </TabsList>
          
          <TabsContent value="skus" className="p-0 m-0">
            {/* Mobile card view (<md): tap-friendly cards instead of dense table */}
            <div className="md:hidden p-3 space-y-2" data-testid="skus-mobile-list">
              {loading ? (
                <p className="text-center py-8 text-muted-foreground text-sm">Loading...</p>
              ) : filteredSkus.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm" data-testid="skus-empty-mobile">
                  {showMissingOnly ? 'All SKUs Dispatch-ready 🎉' : 'No SKUs found'}
                </p>
              ) : filteredSkus.map(sku => (
                <div key={sku.id} className={`border ${selection.isSelected(sku.id) ? 'border-brand bg-blue-50/40' : 'border-border bg-card'} p-3 rounded-md flex gap-3 items-start`} data-testid={`sku-mobile-card-${sku.sku_code}`}>
                  <label className="shrink-0 mt-1 cursor-pointer" aria-label={`Select ${sku.sku_code}`}>
                    <input
                      type="checkbox"
                      data-testid={`select-mobile-sku-${sku.sku_code}`}
                      checked={selection.isSelected(sku.id)}
                      onChange={() => selection.toggle(sku.id)}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setImageDialogSku(sku)}
                    className="shrink-0 w-14 h-14 border border-border bg-muted overflow-hidden rounded-md flex items-center justify-center"
                    title={sku.image_base64 ? 'Replace image' : 'Upload image'}
                  >
                    {sku.image_base64 ? (
                      <img src={sku.image_base64} alt={sku.sku_code} className="w-full h-full object-cover" />
                    ) : (
                      <ImageSquare size={22} className="text-muted-foreground" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-sm font-bold truncate">{sku.sku_code}</span>
                      <Truck size={14} weight="fill" className={isDispatchReady(sku) ? 'text-emerald-600 shrink-0' : 'text-rose-500 shrink-0'} title={isDispatchReady(sku) ? 'Dispatch-ready' : 'Missing weight or volume'} />
                      {(sku.variant_count ?? 0) === 0 && (
                        <span
                          data-testid={`sku-orphan-chip-${sku.sku_code}`}
                          className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded"
                          title="No variant attached — this SKU cannot be added to an order. Click 'Fix orphan SKUs' in the toolbar."
                        >
                          0 variants
                        </span>
                      )}
                      {stockoutRisk[sku.id] && (
                        <span
                          data-testid={`sku-stockout-chip-${sku.sku_code}`}
                          title={`Burn ${stockoutRisk[sku.id].daily_burn}/day · projected stockout ${stockoutRisk[sku.id].projected_stockout_date}`}
                          className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${
                            stockoutRisk[sku.id].risk_level === 'high' ? 'bg-rose-100 text-rose-800 border border-rose-300' :
                            stockoutRisk[sku.id].risk_level === 'medium' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                            'bg-sky-100 text-sky-800 border border-sky-300'
                          }`}
                        >
                          Out by {new Date(stockoutRisk[sku.id].projected_stockout_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                      <AbcChip
                        sku={sku}
                        isAdmin={isAdmin}
                        layout="mobile"
                        onChanged={(updated) => setSkus((prev) => prev.map((s) => s.id === sku.id ? { ...s, abc_class: updated.abc_class, abc_class_locked: updated.abc_class_locked } : s))}
                      />
                    </div>
                    <p className="text-sm truncate text-foreground">{sku.name}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1 font-mono">
                      <span title="Sale price">${sku.unit_price?.toFixed(2)}</span>
                      {(sku.landed_cost || 0) > 0 && (
                        <span title="Landed cost" className="text-amber-700">L ${sku.landed_cost?.toFixed(2)}</span>
                      )}
                      <span className={(sku.weight_kg || 0) === 0 ? 'text-rose-500' : ''}>{sku.weight_kg || 0}kg</span>
                      <span className={(sku.volume_m3 || 0) === 0 ? 'text-rose-500' : ''}>{sku.volume_m3 || 0}m³</span>
                      <span className="uppercase">{sku.inventory_class?.replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      onClick={() => {
                        // Open mobile edit sheet instead of desktop inline-edit
                        setMobileEditSku(sku);
                        setMobileEditFields({
                          sku_code: sku.sku_code || '',
                          barcode: sku.barcode || '',
                          weight_kg: sku.weight_kg || 0,
                          volume_m3: sku.volume_m3 || 0,
                          unit_price: sku.unit_price ?? 0,
                          landed_cost: sku.landed_cost ?? 0,
                        });
                      }}
                      data-testid={`sku-mobile-edit-${sku.sku_code}`}
                      className="p-2.5 text-brand hover:bg-blue-50 rounded-md min-h-10 min-w-10 flex items-center justify-center"
                      title="Edit"
                      aria-label={`Edit ${sku.sku_code}`}
                    ><PencilSimple size={16} /></button>
                    <button
                      onClick={() => printShelfLabel(sku)}
                      data-testid={`sku-mobile-print-${sku.sku_code}`}
                      className="p-2.5 text-foreground hover:bg-muted rounded-md min-h-10 min-w-10 flex items-center justify-center"
                      title="Print shelf label"
                      aria-label={`Print label for ${sku.sku_code}`}
                    ><Printer size={16} /></button>
                    <button
                      onClick={() => handleDeleteSku(sku.id)}
                      data-testid={`sku-mobile-delete-${sku.sku_code}`}
                      className="p-2.5 text-red-600 hover:bg-red-50 rounded-md min-h-10 min-w-10 flex items-center justify-center"
                      title="Delete"
                      aria-label={`Delete ${sku.sku_code}`}
                    ><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto table-scroll" style={{ borderRadius: 0, border: 'none' }}>
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th className="w-10 text-center">
                      <input
                        type="checkbox"
                        aria-label="Select all SKUs on this page"
                        data-testid="select-all-skus"
                        className="cursor-pointer"
                        checked={selection.isAllSelected(filteredSkus)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            selection.selectAll(filteredSkus);
                          } else {
                            selection.unselectAll(filteredSkus);
                          }
                        }}
                      />
                    </th>
                    <th className="w-12 text-center" title="Dispatch Optimiser readiness (weight + volume set)">Ready</th>
                    <th className="w-14 text-center">Img</th>
                    <th>SKU Code</th>
                    <th>Name</th>
                    <th>Barcode</th>
                    <th>Class</th>
                    <th>Price</th>
                    <th title="Landed cost per unit — auto-updated when containers finalize their landed costs">Landed</th>
                    <th>Weight</th>
                    <th>Volume</th>
                    <th title="Min / Max reorder thresholds. Min triggers reorder; Max is the top-up target.">Reorder min/max</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={13} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                  ) : filteredSkus.length === 0 ? (
                    <tr><td colSpan={13} className="text-center py-8 text-muted-foreground" data-testid="skus-empty">
                      {showMissingOnly ? 'All SKUs have weight + volume — catalogue is Dispatch-ready 🎉' : 'No SKUs found'}
                    </td></tr>
                  ) : filteredSkus.map(sku => {
                    const isEditing = editRow === sku.id;
                    const saveEdit = async () => {
                      setEditSaving(true);
                      try {
                        const payload = {
                          weight_kg: editWeight === '' ? 0 : parseFloat(editWeight),
                          volume_m3: editVolume === '' ? 0 : parseFloat(editVolume),
                          unit_price: editPrice === '' ? 0 : parseFloat(editPrice),
                          landed_cost: editLanded === '' ? 0 : parseFloat(editLanded),
                        };
                        // Only include code fields if the user actually changed them — avoids
                        // hitting the uniqueness check with the current value and collapsing into a 409
                        const normSku = (editSkuCode || '').trim().toUpperCase();
                        const normBar = (editBarcode || '').trim().toUpperCase();
                        if (normSku && normSku !== (sku.sku_code || '').toUpperCase()) payload.sku_code = normSku;
                        if (normBar && normBar !== (sku.barcode || '').toUpperCase()) payload.barcode = normBar;
                        // iter168 — Reorder thresholds: send min ALWAYS (server normalises blank→0
                        // = "no threshold"). Send max ONLY when the operator typed a value; an empty
                        // input clears the cap by sending null which the backend $unset's effectively
                        // via the allowed-update list.
                        payload.reorder_point = editReorderMin === '' ? 0 : Math.max(0, parseInt(editReorderMin, 10) || 0);
                        if (editReorderMax === '') {
                          payload.reorder_max = null;
                        } else {
                          payload.reorder_max = Math.max(0, parseInt(editReorderMax, 10) || 0);
                        }

                        await updateSku(sku.id, payload);
                        toast.success(`${sku.sku_code} updated`);
                        setEditRow(null); setEditSkuCode(''); setEditBarcode(''); setEditWeight(''); setEditVolume(''); setEditPrice(''); setEditLanded(''); setEditReorderMin(''); setEditReorderMax('');
                        fetchData();
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || 'Update failed');
                      } finally { setEditSaving(false); }
                    };
                    return (
                    <tr key={sku.id} className={selection.isSelected(sku.id) ? 'bg-blue-50/40' : ''}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          aria-label={`Select ${sku.sku_code}`}
                          data-testid={`select-sku-${sku.sku_code}`}
                          className="cursor-pointer"
                          checked={selection.isSelected(sku.id)}
                          onChange={() => selection.toggle(sku.id)}
                        />
                      </td>
                      <td className="text-center" data-testid={`dispatch-ready-${sku.sku_code}`}>
                        {isDispatchReady(sku) ? (
                          <span title="Dispatch-ready (weight + volume set)" className="inline-flex items-center justify-center">
                            <Truck size={16} weight="fill" className="text-emerald-600" />
                          </span>
                        ) : (
                          <span title="Missing weight or volume — won't appear in Dispatch Optimiser" className="inline-flex items-center justify-center">
                            <Truck size={16} weight="fill" className="text-rose-500" />
                          </span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => setImageDialogSku(sku)}
                          data-testid={`sku-image-open-${sku.sku_code}`}
                          className="inline-flex items-center justify-center w-10 h-10 border border-border bg-muted hover:border-brand overflow-hidden"
                          title={sku.image_base64 ? 'Replace image' : 'Upload image'}
                        >
                          {sku.image_base64 ? (
                            <img src={sku.image_base64} alt={sku.sku_code} className="w-full h-full object-cover" />
                          ) : (
                            <ImageSquare size={18} className="text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="font-mono text-sm font-semibold">
                        {isEditing ? (
                          <Input
                            type="text" value={editSkuCode}
                            onChange={(e) => setEditSkuCode(e.target.value)}
                            data-testid={`edit-sku-code-${sku.sku_code}`}
                            className="h-7 w-36 text-xs font-mono" maxLength={64} autoFocus
                            placeholder={sku.sku_code}
                          />
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            {sku.sku_code}
                            {(sku.variant_count ?? 0) === 0 && (
                              <span
                                data-testid={`sku-orphan-chip-${sku.sku_code}`}
                                className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 rounded"
                                title="No variant attached — this SKU cannot be added to an order. Click 'Fix orphan SKUs' in the toolbar."
                              >
                                0 variants
                              </span>
                            )}
                            {stockoutRisk[sku.id] && (
                              <span
                                data-testid={`sku-stockout-chip-${sku.sku_code}`}
                                title={`Burn ${stockoutRisk[sku.id].daily_burn}/day · ${stockoutRisk[sku.id].current_qty} on-hand · projected stockout ${stockoutRisk[sku.id].projected_stockout_date}`}
                                className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded ${
                                  stockoutRisk[sku.id].risk_level === 'high' ? 'bg-rose-100 text-rose-800 border border-rose-300' :
                                  stockoutRisk[sku.id].risk_level === 'medium' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                                  'bg-sky-100 text-sky-800 border border-sky-300'
                                }`}
                              >
                                Out by {new Date(stockoutRisk[sku.id].projected_stockout_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                            <AbcChip
                              sku={sku}
                              isAdmin={isAdmin}
                              layout="desktop"
                              onChanged={(updated) => setSkus((prev) => prev.map((s) => s.id === sku.id ? { ...s, abc_class: updated.abc_class, abc_class_locked: updated.abc_class_locked } : s))}
                            />
                          </span>
                        )}
                      </td>
                      <td>{sku.name}</td>
                      <td>
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="text" value={editBarcode}
                              onChange={(e) => setEditBarcode(e.target.value)}
                              data-testid={`edit-barcode-${sku.sku_code}`}
                              className="h-7 w-40 text-xs font-mono" maxLength={64}
                              placeholder={sku.barcode}
                            />
                            <button
                              type="button"
                              onClick={() => { setScanTarget(sku.id); setScannerOpen(true); }}
                              data-testid={`edit-barcode-scan-${sku.sku_code}`}
                              className="p-1 text-brand hover:bg-blue-50 rounded"
                              title="Scan barcode"
                              aria-label="Scan barcode"
                            ><Camera size={14} weight="bold" /></button>
                          </div>
                        ) : (
                          <span className="font-mono text-xs bg-muted px-2 py-1">{sku.barcode}</span>
                        )}
                      </td>
                      <td><span className="text-xs uppercase font-bold text-muted-foreground">{sku.inventory_class?.replace('_', ' ')}</span></td>
                      <td className="font-mono">
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            data-testid={`edit-price-${sku.sku_code}`}
                            className="h-7 w-24 text-xs"
                            placeholder={String(sku.unit_price ?? 0)}
                          />
                        ) : (
                          <span>${(sku.unit_price ?? 0).toFixed(2)}</span>
                        )}
                      </td>
                      <td className="font-mono">
                        {isEditing ? (
                          <Input
                            type="number"
                            step="0.01"
                            min={0}
                            value={editLanded}
                            onChange={(e) => setEditLanded(e.target.value)}
                            data-testid={`edit-landed-${sku.sku_code}`}
                            className="h-7 w-24 text-xs"
                            placeholder={String(sku.landed_cost ?? 0)}
                          />
                        ) : (
                          <span className={(sku.landed_cost || 0) === 0 ? 'text-muted-foreground' : ''} title="Landed cost per unit">
                            ${(sku.landed_cost ?? 0).toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="font-mono">
                        {isEditing ? (
                          <Input type="number" step="0.001" value={editWeight} onChange={(e) => setEditWeight(e.target.value)} data-testid={`edit-weight-${sku.sku_code}`} className="h-7 w-20 text-xs" />
                        ) : (
                          <span className={(sku.weight_kg || 0) === 0 ? 'text-muted-foreground' : ''}>{sku.weight_kg || 0} kg</span>
                        )}
                      </td>
                      <td className="font-mono">
                        {isEditing ? (
                          <Input type="number" step="0.001" value={editVolume} onChange={(e) => setEditVolume(e.target.value)} data-testid={`edit-volume-${sku.sku_code}`} className="h-7 w-20 text-xs" />
                        ) : (
                          <span className={(sku.volume_m3 || 0) === 0 ? 'text-muted-foreground' : ''}>{sku.volume_m3 || 0} m³</span>
                        )}
                      </td>
                      {/* iter168 — Inline edit of reorder min/max thresholds per SKU */}
                      <td className="font-mono" data-testid={`reorder-cell-${sku.sku_code}`}>
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number" min={0} step={1}
                              value={editReorderMin}
                              onChange={(e) => setEditReorderMin(e.target.value)}
                              placeholder="min"
                              className="h-7 w-14 text-xs font-mono"
                              data-testid={`edit-reorder-min-${sku.sku_code}`}
                            />
                            <span className="text-muted-foreground text-[10px]">/</span>
                            <Input
                              type="number" min={0} step={1}
                              value={editReorderMax}
                              onChange={(e) => setEditReorderMax(e.target.value)}
                              placeholder="max"
                              className="h-7 w-14 text-xs font-mono"
                              data-testid={`edit-reorder-max-${sku.sku_code}`}
                            />
                          </div>
                        ) : (
                          <div className="text-xs leading-tight">
                            <span className={(sku.reorder_point || 0) === 0 ? 'text-muted-foreground' : 'font-semibold'} title="Min — trigger reorder">
                              {sku.reorder_point || 0}
                            </span>
                            <span className="text-muted-foreground mx-1">/</span>
                            <span className={sku.reorder_max == null ? 'text-muted-foreground' : 'font-semibold'} title="Max — top-up target">
                              {sku.reorder_max != null ? sku.reorder_max : '—'}
                            </span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {isEditing ? (
                            <>
                              <button onClick={saveEdit} disabled={editSaving} data-testid={`save-wv-${sku.sku_code}`} className="p-1.5 hover:bg-emerald-50 text-emerald-700 disabled:opacity-50" title="Save"><Check size={16} weight="bold" /></button>
                              <button onClick={() => { setEditRow(null); setEditSkuCode(''); setEditBarcode(''); setEditWeight(''); setEditVolume(''); setEditPrice(''); setEditLanded(''); setEditReorderMin(''); setEditReorderMax(''); }} className="p-1.5 hover:bg-muted" title="Cancel"><X size={16} /></button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditRow(sku.id);
                                  setEditSkuCode(sku.sku_code || '');
                                  setEditBarcode(sku.barcode || '');
                                  setEditWeight(sku.weight_kg || 0);
                                  setEditVolume(sku.volume_m3 || 0);
                                  setEditPrice(sku.unit_price ?? 0);
                                  setEditLanded(sku.landed_cost ?? 0);
                                  // iter168 — populate min/max from the current SKU doc
                                  setEditReorderMin(sku.reorder_point != null ? String(sku.reorder_point) : '');
                                  setEditReorderMax(sku.reorder_max != null ? String(sku.reorder_max) : '');
                                }}
                                data-testid={`edit-wv-${sku.sku_code}`}
                                className="p-1.5 hover:bg-blue-50 text-brand" title="Edit row"
                              ><PencilSimple size={16} /></button>
                              <button
                                onClick={() => printShelfLabel(sku)}
                                data-testid={`print-label-${sku.sku_code}`}
                                className="p-1.5 hover:bg-muted text-foreground"
                                title="Print shelf label"
                              ><Printer size={16} /></button>
                              <button
                                onClick={() => setPriceHistorySku(sku)}
                                data-testid={`price-history-${sku.sku_code}`}
                                className="p-1.5 hover:bg-muted text-foreground"
                                title="Price history"
                              ><ClockClockwise size={16} /></button>
                              <SkuTierPricePreview skuId={sku.id} skuCode={sku.sku_code} />
                              <button
                                className="p-1.5 hover:bg-muted text-foreground"
                                title="Manage UOMs / pack sizes"
                                onClick={() => setManageUomsSku(sku)}
                                data-testid={`manage-uoms-${sku.sku_code}`}
                              ><Ruler size={16} /></button>
                              <button className="p-1.5 hover:bg-muted" title="View"><Eye size={16} /></button>
                              <button className="p-1.5 hover:bg-red-50 text-red-600" title="Delete" onClick={() => handleDeleteSku(sku.id)}><Trash size={16} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="variants" className="p-0 m-0">
            {/* Show-inactive filter strip — only renders when at least one
                variant has been deactivated, so the toolbar stays clean for
                tenants who never use the feature. */}
            {(inactiveCount > 0 || showInactive) && (
              <div className="px-4 pt-3 flex items-center gap-2" data-testid="variant-show-inactive-strip">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    data-testid="variant-show-inactive-toggle"
                    className="rounded border-input"
                  />
                  <span className="font-semibold text-foreground">Show deactivated variants</span>
                  <span className="text-muted-foreground tabular-nums">({inactiveCount})</span>
                </label>
              </div>
            )}

            {/* Mobile card view */}
            <div className="md:hidden p-3 space-y-2" data-testid="variants-mobile-list">
              {filteredVariants.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm" data-testid="variants-empty-mobile">
                  {showMissingOnly ? 'All variants Dispatch-ready 🎉' : 'No variants found'}
                </p>
              ) : filteredVariants.map(v => {
                const parent = skusMap[v.sku_id];
                const ready = isDispatchReady(parent);
                const isInactive = v.is_active === false;
                return (
                  <div
                    key={v.id}
                    className={`border bg-card p-3 rounded-md flex items-center justify-between gap-3 ${
                      isInactive ? 'border-dashed border-muted-foreground/40 opacity-70' : 'border-border'
                    }`}
                    data-testid={`variant-mobile-card-${v.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold truncate">{parent ? parent.sku_code : v.sku_id?.slice(-8)}</span>
                        <Truck size={14} weight="fill" className={ready ? 'text-emerald-600 shrink-0' : 'text-rose-500 shrink-0'} />
                        {isInactive && (
                          <span className="text-[9px] px-1 py-0.5 bg-muted text-muted-foreground font-bold uppercase tracking-widest">Deactivated</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap gap-x-2">
                        {v.size && <span>Size: <b>{v.size}</b></span>}
                        {v.color && <span>Color: <b>{v.color}</b></span>}
                        {v.style && <span>Style: <b>{v.style}</b></span>}
                      </div>
                      <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 inline-block mt-1">{v.barcode}</span>
                    </div>
                    <Switch
                      checked={!isInactive}
                      disabled={togglingActive === v.id}
                      onCheckedChange={() => toggleVariantActive(v)}
                      data-testid={`variant-active-toggle-${v.id}`}
                      title={isInactive ? 'Activate' : 'Deactivate (hide from order picker)'}
                    />
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto table-scroll" style={{ borderRadius: 0, border: 'none' }}>
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th className="w-8 text-center">
                      <input
                        type="checkbox"
                        checked={variantSelection.isAllSelected(filteredVariants)}
                        onChange={() => variantSelection.toggleAll(filteredVariants)}
                        data-testid="variant-select-all"
                        className="rounded border-input"
                        title={variantSelection.isAllSelected(filteredVariants) ? 'Unselect all' : 'Select all visible'}
                      />
                    </th>
                    <th className="w-12 text-center" title="Dispatch Optimiser readiness (parent SKU has weight + volume)">Ready</th>
                    <th>Barcode</th>
                    <th>Parent SKU</th>
                    <th>Size</th>
                    <th>Color</th>
                    <th>Style</th>
                    <th className="w-20 text-center" title="Active variants are sellable on orders. Deactivated variants stay in the system but are hidden from the order picker.">Active</th>
                    <th className="w-16">History</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVariants.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-muted-foreground" data-testid="variants-empty">
                      {showMissingOnly ? 'All variants trace to a Dispatch-ready SKU 🎉' : 'No variants found'}
                    </td></tr>
                  ) : filteredVariants.map(v => {
                    const parent = skusMap[v.sku_id];
                    const ready = isDispatchReady(parent);
                    const isInactive = v.is_active === false;
                    const checked = variantSelection.isSelected(v.id);
                    return (
                    <tr key={v.id} className={`${isInactive ? 'opacity-60' : ''} ${checked ? 'bg-blue-50/40' : ''}`}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => variantSelection.toggle(v.id)}
                          data-testid={`variant-row-select-${v.id}`}
                          className="rounded border-input"
                        />
                      </td>
                      <td className="text-center" data-testid={`variant-dispatch-ready-${v.id}`}>
                        <span title={ready ? 'Parent SKU has weight + volume' : 'Parent SKU missing weight or volume'} className="inline-flex items-center justify-center">
                          <Truck size={16} weight="fill" className={ready ? 'text-emerald-600' : 'text-rose-500'} />
                        </span>
                      </td>
                      <td>
                        <span className="font-mono text-xs bg-muted px-2 py-1">{v.barcode}</span>
                        {isInactive && (
                          <span className="ml-2 text-[9px] px-1 py-0.5 bg-muted text-muted-foreground font-bold uppercase tracking-widest" data-testid={`variant-inactive-badge-${v.id}`}>
                            Deactivated
                          </span>
                        )}
                      </td>
                      <td className="font-mono text-sm">{parent ? `${parent.sku_code}` : v.sku_id?.slice(-8)}</td>
                      <td>{v.size || '-'}</td>
                      <td>{v.color || '-'}</td>
                      <td>{v.style || '-'}</td>
                      <td className="text-center">
                        <Switch
                          checked={!isInactive}
                          disabled={togglingActive === v.id || variantBulkBusy}
                          onCheckedChange={() => toggleVariantActive(v)}
                          data-testid={`variant-active-toggle-${v.id}`}
                          title={isInactive ? 'Activate' : 'Deactivate (hide from order picker)'}
                        />
                      </td>
                      <td className="text-center">
                        <button
                          onClick={() => setStockHistoryVariant({ variant: v, sku: parent })}
                          data-testid={`stock-history-${v.id}`}
                          className="p-1.5 hover:bg-muted text-foreground inline-flex"
                          title="Stock-level history"
                        ><ChartLineUp size={16} /></button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Sticky selection action bar — surfaces when 1+ SKU is selected. */}
      <BulkActionsBar
        count={selection.count}
        onClear={selection.clear}
        actions={[
          {
            key: 'reprice',
            label: 'Reprice',
            icon: <CurrencyDollar size={14} weight="fill" />,
            onClick: () => setBulkPriceOpen(true),
            testId: 'bulk-reprice-skus-btn',
            title: `Apply +%, set price, or shift price for ${selection.count} SKU${selection.count === 1 ? '' : 's'}`,
          },
          {
            key: 'print',
            label: 'Print labels',
            icon: <Printer size={14} weight="fill" />,
            onClick: printSelectedLabels,
            testId: 'bulk-print-labels-btn',
            title: `Print ${selection.count} shelf labels`,
          },
        ]}
      />

      {/* Variants bulk actions — shows once at least one variant row is
          checked. Lives below the SKU bar but the BulkActionsBar component
          self-hides at count=0 so they never overlap. */}
      <BulkActionsBar
        count={variantSelection.count}
        onClear={variantSelection.clear}
        actions={[
          {
            key: 'variant-deactivate',
            label: variantBulkBusy ? 'Saving…' : (anySelectedActive ? 'Deactivate' : 'Activate'),
            icon: <X size={14} weight="bold" />,
            onClick: () => bulkToggleVariants(!anySelectedActive),
            testId: 'bulk-variant-active-toggle-btn',
            title: anySelectedActive
              ? `Hide ${variantSelection.count} variant${variantSelection.count === 1 ? '' : 's'} from the order picker`
              : `Reactivate ${variantSelection.count} variant${variantSelection.count === 1 ? '' : 's'}`,
            disabled: variantBulkBusy,
          },
        ]}
      />
      <BulkPriceDialog
        open={bulkPriceOpen}
        onOpenChange={setBulkPriceOpen}
        count={selection.count}
        onApply={handleBulkPriceApply}
      />
      <PriceHistoryDialog
        sku={priceHistorySku}
        open={!!priceHistorySku}
        onOpenChange={(v) => !v && setPriceHistorySku(null)}
      />
      <StockHistoryDialog
        variant={stockHistoryVariant?.variant}
        sku={stockHistoryVariant?.sku}
        open={!!stockHistoryVariant}
        onOpenChange={(v) => !v && setStockHistoryVariant(null)}
      />

      {/* SKU image upload dialog */}
      <SkuImageDialog
        sku={imageDialogSku}
        onClose={() => setImageDialogSku(null)}
        onUploaded={(img) => {
          // Reflect locally so the thumbnail button in the row updates
          setSkus((prev) => prev.map((s) => s.id === imageDialogSku.id ? { ...s, image_base64: img } : s));
        }}
      />
      {/* iter164 — Manage UOM conversions dialog */}
      <ManageSkuUomsDialog
        open={!!manageUomsSku}
        onOpenChange={(v) => !v && setManageUomsSku(null)}
        sku={manageUomsSku}
        onSaved={() => fetchData()}
      />
      {/* Mobile bottom-sheet edit — phone-friendly alternative to inline-edit.
          Edits sku_code, barcode, weight_kg, volume_m3 in one sheet with a Camera
          scan button on the barcode input. Triggered by the pencil icon on mobile
          SKU cards (desktop table still uses the inline-edit row). */}
      <Sheet open={!!mobileEditSku} onOpenChange={(v) => !v && setMobileEditSku(null)}>
        <SheetContent side="bottom" className="h-[92vh] rounded-t-xl md:max-w-md md:mx-auto" data-testid="mobile-edit-sheet">
          <SheetHeader className="text-left mb-4">
            <SheetTitle className="text-lg">Edit SKU</SheetTitle>
            <SheetDescription className="text-xs">
              {mobileEditSku?.name || ''}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline">SKU Code</Label>
              <Input
                value={mobileEditFields.sku_code}
                onChange={(e) => setMobileEditFields((p) => ({ ...p, sku_code: e.target.value }))}
                className="mt-1 font-mono"
                data-testid="mobile-edit-sku-code"
                maxLength={64}
              />
            </div>
            <div>
              <Label className="overline">Barcode</Label>
              <div className="mt-1 flex gap-1">
                <Input
                  value={mobileEditFields.barcode}
                  onChange={(e) => setMobileEditFields((p) => ({ ...p, barcode: e.target.value }))}
                  className="font-mono flex-1"
                  data-testid="mobile-edit-barcode"
                  maxLength={64}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // Close sheet so its overlay doesn't trap pointer events;
                    // stash the sku + fields so we can restore on scan completion.
                    setPendingMobileSku(mobileEditSku);
                    setMobileEditSku(null);
                    setScanTarget('mobileEdit');
                    setScannerOpen(true);
                  }}
                  data-testid="mobile-edit-barcode-scan"
                  title="Scan barcode with camera"
                  className="shrink-0 px-3"
                  aria-label="Scan barcode"
                >
                  <Camera size={16} weight="bold" />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Sale Price ($)</Label>
                <Input
                  type="number" step="0.01" min={0} inputMode="decimal"
                  value={mobileEditFields.unit_price}
                  onChange={(e) => setMobileEditFields((p) => ({ ...p, unit_price: e.target.value }))}
                  className="mt-1 font-mono"
                  data-testid="mobile-edit-price"
                />
              </div>
              <div>
                <Label className="overline">Landed Cost ($)</Label>
                <Input
                  type="number" step="0.01" min={0} inputMode="decimal"
                  value={mobileEditFields.landed_cost}
                  onChange={(e) => setMobileEditFields((p) => ({ ...p, landed_cost: e.target.value }))}
                  className="mt-1 font-mono"
                  data-testid="mobile-edit-landed"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Weight (kg)</Label>
                <Input
                  type="number" step="0.001" inputMode="decimal"
                  value={mobileEditFields.weight_kg}
                  onChange={(e) => setMobileEditFields((p) => ({ ...p, weight_kg: e.target.value }))}
                  className="mt-1 font-mono"
                  data-testid="mobile-edit-weight"
                />
              </div>
              <div>
                <Label className="overline">Volume (m³)</Label>
                <Input
                  type="number" step="0.001" inputMode="decimal"
                  value={mobileEditFields.volume_m3}
                  onChange={(e) => setMobileEditFields((p) => ({ ...p, volume_m3: e.target.value }))}
                  className="mt-1 font-mono"
                  data-testid="mobile-edit-volume"
                />
              </div>
            </div>
          </div>
          <SheetFooter className="flex-col gap-2 pt-6 mt-auto">
            <Button
              onClick={async () => {
                setMobileEditSaving(true);
                try {
                  const payload = {
                    weight_kg: parseFloat(mobileEditFields.weight_kg) || 0,
                    volume_m3: parseFloat(mobileEditFields.volume_m3) || 0,
                    unit_price: parseFloat(mobileEditFields.unit_price) || 0,
                    landed_cost: parseFloat(mobileEditFields.landed_cost) || 0,
                  };
                  const normSku = (mobileEditFields.sku_code || '').trim().toUpperCase();
                  const normBar = (mobileEditFields.barcode || '').trim().toUpperCase();
                  if (normSku && normSku !== (mobileEditSku.sku_code || '').toUpperCase()) payload.sku_code = normSku;
                  if (normBar && normBar !== (mobileEditSku.barcode || '').toUpperCase()) payload.barcode = normBar;
                  await updateSku(mobileEditSku.id, payload);
                  toast.success(`${mobileEditSku.sku_code} updated`);
                  // Print label with the FRESH values the user just saved
                  printShelfLabel({
                    ...mobileEditSku,
                    sku_code: payload.sku_code || mobileEditSku.sku_code,
                    barcode: payload.barcode || mobileEditSku.barcode,
                    weight_kg: payload.weight_kg,
                    volume_m3: payload.volume_m3,
                  });
                  setMobileEditSku(null);
                  fetchData();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || 'Update failed');
                } finally {
                  setMobileEditSaving(false);
                }
              }}
              disabled={mobileEditSaving}
              className="w-full h-11 bg-brand hover:bg-brand/90 text-brand-foreground"
              data-testid="mobile-edit-save-print"
            >
              <Printer size={16} className="mr-2" weight="fill" />
              {mobileEditSaving ? 'Saving…' : 'Save & Print Label'}
            </Button>
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                onClick={() => setMobileEditSku(null)}
                className="flex-1 h-11"
                data-testid="mobile-edit-cancel"
              >Cancel</Button>
              <Button
                variant="outline"
                onClick={async () => {
                  setMobileEditSaving(true);
                  try {
                    const payload = {
                      weight_kg: parseFloat(mobileEditFields.weight_kg) || 0,
                      volume_m3: parseFloat(mobileEditFields.volume_m3) || 0,
                    };
                    const normSku = (mobileEditFields.sku_code || '').trim().toUpperCase();
                    const normBar = (mobileEditFields.barcode || '').trim().toUpperCase();
                    if (normSku && normSku !== (mobileEditSku.sku_code || '').toUpperCase()) payload.sku_code = normSku;
                    if (normBar && normBar !== (mobileEditSku.barcode || '').toUpperCase()) payload.barcode = normBar;
                    await updateSku(mobileEditSku.id, payload);
                    toast.success(`${mobileEditSku.sku_code} updated`);
                    setMobileEditSku(null);
                    fetchData();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Update failed');
                  } finally {
                    setMobileEditSaving(false);
                  }
                }}
                disabled={mobileEditSaving}
                className="flex-1 h-11"
                data-testid="mobile-edit-save"
              >{mobileEditSaving ? 'Saving…' : 'Save'}</Button>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Barcode scanner overlay — dispatches to the right target based on scanTarget.
          Dialog is closed before opening (for 'new' target) to avoid Radix Dialog's overlay
          intercepting pointer events behind the scanner. */}
      {scannerOpen && (
        <BarcodeScanner
          title={scanTarget === 'new' ? 'Scan SKU Barcode' : scanTarget === 'newVariant' ? 'Scan Variant Barcode' : 'Scan Barcode — inline edit'}
          onScan={(code) => {
            if (scanTarget === 'new') {
              setNewSku((prev) => ({ ...prev, barcode: code }));
              setShowCreateSku(true);
            } else if (scanTarget === 'newVariant') {
              setNewVariant((prev) => ({ ...prev, barcode: code }));
              setShowCreateVariant(true);
            } else if (scanTarget === 'mobileEdit') {
              setMobileEditFields((prev) => ({ ...prev, barcode: code }));
              // Restore the sheet — the sku was stashed in pendingMobileSku
              if (pendingMobileSku) setMobileEditSku(pendingMobileSku);
              setPendingMobileSku(null);
            } else if (scanTarget) {
              // Inline-edit target — scanTarget holds the sku.id
              setEditBarcode(code);
              toast.message(`Barcode captured: ${code}`);
            }
            setScannerOpen(false);
            setScanTarget(null);
          }}
          onClose={() => {
            if (scanTarget === 'new') setShowCreateSku(true);
            if (scanTarget === 'newVariant') setShowCreateVariant(true);
            if (scanTarget === 'mobileEdit' && pendingMobileSku) {
              setMobileEditSku(pendingMobileSku);
              setPendingMobileSku(null);
            }
            setScannerOpen(false);
            setScanTarget(null);
          }}
        />
      )}

      {/* Bulk Scan & Onboard — keeps scanner open; each scan creates a draft SKU */}
      {bulkScanOpen && (
        <BulkScanOnboard
          onboardedList={bulkOnboarded}
          onOnboarded={(entry) => setBulkOnboarded((prev) => [entry, ...prev])}
          onClose={() => {
            setBulkScanOpen(false);
            if (bulkOnboarded.length > 0) {
              toast.success(`Onboarded ${bulkOnboarded.length} SKU${bulkOnboarded.length === 1 ? '' : 's'} — edit names/dims in the table`);
              fetchData();
              setBulkOnboarded([]);
            }
          }}
        />
      )}
    </Layout>
  );
}
